import { useEffect, useState, useCallback } from 'react';
// @mui
import Container from '@mui/material/Container';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
// routes
import { paths } from 'src/routes/paths';
import { useParams, useRouter } from 'src/routes/hook';
// api
import { vuonTrongApiService } from 'src/api/vuontrong-api';
// types
import { VuonTrong } from 'src/types/vuontrong';
// components
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import EmptyContent from 'src/components/empty-content';
import { useSnackbar } from 'src/components/snackbar';
// validation
import { number } from 'yup';
//
import VuonTrongNewEditForm from '../vuontrong-new-edit-form';

// ----------------------------------------------------------------------

export default function EditVuonTrongView() {
  const settings = useSettingsContext();
  const params = useParams();
  const { id } = params;
  const router = useRouter();
  const { enqueueSnackbar } = useSnackbar();

  const [vuonTrong, setVuonTrong] = useState<VuonTrong | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchVuonTrong = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    try {
      const response = await vuonTrongApiService.getVuonTrong(Number(id)); 
      if (response.messCode === 7 && response.oneItem) {
        setVuonTrong(response.oneItem);
      }
    } catch (error) {
      console.error('Error fetching vuon trong:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchVuonTrong();
  }, [fetchVuonTrong]);

  if (loading) {
    return (
      <Container maxWidth={settings.themeStretch ? false : 'lg'}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!vuonTrong) {
    return (
      <Container maxWidth={settings.themeStretch ? false : 'lg'}>
        <EmptyContent
          filled
          title="Không tìm thấy vườn trồng"
          sx={{ py: 10 }}
        />
      </Container>
    );
  }

  const handleSubmit = async (data:VuonTrong) => {
    try {
      const response = await vuonTrongApiService.editVuonTrong(data);
      if (response.messCode === 1 || response.messCode === 7) {
        enqueueSnackbar('Cập nhật vườn trồng thành công!', { variant: 'success' });
        router.push(paths.dashboard.vuontrong.list);
      } else {
        enqueueSnackbar(response.message || 'Cập nhật thất bại', { variant: 'error' });
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Có lỗi xảy ra khi cập nhật', { variant: 'error' });
    }
  };

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Chỉnh sửa vườn trồng"
        links={[
          {
            name: 'Dashboard',
            href: paths.dashboard.root,
          },
          {
            name: 'Vườn trồng',
            href: paths.dashboard.vuontrong.root,
          },
          { name: vuonTrong.tenVuon || 'Chỉnh sửa' },
        ]}
        sx={{
          mb: { xs: 3, md: 5 },
        }}
      />
      
      <VuonTrongNewEditForm current={vuonTrong} onSubmitForm={handleSubmit} />
    </Container>
  );
}
